/**
 * CWPP API module - Cloud Workload Protection Platform
 *
 * Real per-domain split: contains the actual implementations of the CWPP
 * domain API objects, extracted verbatim from the monolithic
 * frontend/src/services/api.ts. This file replaces the prior stub that
 * re-exported from `../api`.
 *
 * Domain objects:
 *   - cwppApi            (workloads, runtime, FIM, threats, SBOM, patch mgmt, forensics, segmentation)
 *   - kspmApi            (KSPM clusters/findings/workloads/RBAC/network/images/runtime/compliance/admission)
 *   - kspmClusterApi     (cluster register/list/delete shorthand)
 *   - snapshotScanApi    (agentless snapshot scanning - packages/vulns/secrets/misconfigs)
 *   - imageDriftApi      (container image drift policies/events/baselines)
 *   - runtimeSensorApi   (runtime sensor dashboard/sensors/alerts/threat-rules)
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';
import type {
  Workload,
  WorkloadDetail,
  WorkloadType,
  WorkloadVulnerability,
  RuntimeEvent,
  RuntimeEventType,
  RuntimeEventAction,
  Threat,
  ThreatType,
  ThreatStatus,
  SeverityLevel,
  CloudProvider,
} from '../../types';

/**
 * R138: EKS rows in csp_cwpp.workloads carry a workload_id shaped
 * "<cluster>/<nodegroup>" (e.g. "wellzio-dfd/dfd-dev-worker-node"). A slash
 * cannot survive a path segment: the gateway/uvicorn decodes %2F back to "/"
 * before routing, so the request gains an extra segment and no route matches.
 * Every per-workload sub-resource call for those two rows was therefore a
 * guaranteed 404 on page load. Detect them and fail fast with a clear error
 * instead of emitting requests that cannot succeed.
 *
 * The real fix belongs on the backend — expose a path-safe id, or accept
 * workload_id as a query parameter — and is tracked separately.
 */
const isPathSafeWorkloadId = (id: string): boolean => !!id && !id.includes('/');

const unroutableWorkloadId = (id: string): Error => {
  const err = new Error(
    `workload id "${id}" contains "/" and cannot be addressed as a path segment`,
  ) as Error & { status?: number; unroutableId?: boolean };
  err.status = 400;
  err.unroutableId = true;
  return err;
};

export const cwppApi = {
  // Workloads
  getWorkloads: async (params?: {
    workload_type?: WorkloadType;
    provider?: CloudProvider;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ workloads: Workload[]; total: number }> => {
    // R197al: cwpp-aggregator's /api/v1/workloads handler accepts
    // `limit`/`offset`, NOT `page_size`. With an unexpected page_size
    // query param it silently returns total=0 (defensive shape but
    // wrong from the FE's perspective). Translate page_size→limit
    // here so the workloads tab actually populates.
    const { page_size, page, ...rest } = params || {};
    const mapped: any = { ...rest };
    if (page_size != null) mapped.limit = page_size;
    if (page != null && page_size != null) mapped.offset = (page - 1) * page_size;
    const res = _unwrap(await fetchClient.get<{ workloads: Workload[]; total: number }>('/api/v1/cwpp/workloads', mapped));
    // BE (cwpp-aggregator) returns workloads keyed as workload_id/cloud_provider/
    // vulnerabilities_count/critical_vulnerabilities/high_vulnerabilities/
    // protection_status. The FE expects id/provider/vulnerability_count/
    // critical_vuln_count/high_vuln_count/agent_installed. Remap here (keeping
    // the original keys via spread) so row keys, drill-in ids, provider
    // badges/filters and the stat cards all read correctly.
    const workloads = ((res as any)?.workloads || []).map((w: any) => ({
      ...w,
      id: w.id ?? w.workload_id,
      provider: w.provider ?? w.cloud_provider,
      vulnerability_count: w.vulnerability_count ?? w.vulnerabilities_count ?? 0,
      critical_vuln_count: w.critical_vuln_count ?? w.critical_vulnerabilities ?? 0,
      high_vuln_count: w.high_vuln_count ?? w.high_vulnerabilities ?? 0,
      agent_installed: w.agent_installed ?? (w.protection_status === 'protected'),
    }));
    return { ...(res as any), workloads } as { workloads: Workload[]; total: number };
  },

  getWorkload: async (id: string): Promise<Workload> => {
    return _unwrap(await fetchClient.get<Workload>(`/api/v1/cwpp/workloads/${encodeURIComponent(id)}`));
  },

  // R138: CWPP (:8005) has no `/workloads/{id}/detail` route — it was never
  // implemented, so every call 404'd (visible as console noise on the CWPP
  // Workloads tab). The base `/workloads/{id}` DOES return the full workload
  // record, so fall back to it instead of surfacing a dead endpoint.
  getWorkloadDetail: async (id: string): Promise<WorkloadDetail> => {
    if (!isPathSafeWorkloadId(id)) throw unroutableWorkloadId(id);
    const enc = encodeURIComponent(id);
    const res = await fetchClient.get<WorkloadDetail>(`/api/v1/cwpp/workloads/${enc}/detail`);
    if (res.error && res.status === 404) {
      return _unwrap(await fetchClient.get<WorkloadDetail>(`/api/v1/cwpp/workloads/${enc}`));
    }
    return _unwrap(res);
  },

  getHighRiskWorkloads: async (minScore?: number): Promise<{ workloads: Workload[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ workloads: Workload[]; total: number }>('/api/v1/cwpp/workloads/high-risk', { min_score: minScore } as any));
  },

  getInternetExposedWorkloads: async (): Promise<{ workloads: Workload[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ workloads: Workload[]; total: number }>('/api/v1/cwpp/workloads/internet-exposed'));
  },

  // Vulnerability Scanner
  getWorkloadVulnerabilities: async (
    workloadId: string,
    params?: { severity?: SeverityLevel }
  ): Promise<{ vulnerabilities: WorkloadVulnerability[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ vulnerabilities: WorkloadVulnerability[]; total: number }>(`/api/v1/cwpp/workloads/${encodeURIComponent(workloadId)}/vulnerabilities`, params as any));
  },

  triggerVulnScan: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cwpp/vulnerabilities/scan`, {
      workload_id: workloadId,
    }));
  },

  // Runtime Protection
  getRuntimeEvents: async (params?: {
    workload_id?: string;
    event_type?: RuntimeEventType;
    action?: RuntimeEventAction;
    severity?: SeverityLevel;
    limit?: number;
  }): Promise<{ events: RuntimeEvent[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ events: RuntimeEvent[]; total: number }>('/api/v1/cwpp/runtime/events', params as any));
  },

  getRuntimeStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cwpp/runtime/statistics'));
  },

  getRuntimePolicies: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/runtime/policies'));
  },

  toggleRuntimePolicy: async (policyId: string, enabled: boolean): Promise<any> => {
    const qs = new URLSearchParams({ enabled: String(enabled) }).toString();
    return _unwrap(await fetchClient.put(`/api/v1/cwpp/runtime/policies/${policyId}/toggle?${qs}`, null));
  },

  // File Integrity Monitoring
  getFIMBaselines: async (workloadId: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/fim/baselines/${workloadId}`));
  },

  getFIMChanges: async (workloadId: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/fim/changes/${workloadId}`));
  },

  // Threat Detection
  getThreats: async (params?: {
    workload_id?: string;
    threat_type?: ThreatType;
    status?: ThreatStatus;
    severity?: SeverityLevel;
    limit?: number;
  }): Promise<{ threats: Threat[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ threats: Threat[]; total: number }>('/api/v1/cwpp/threats', params as any));
  },

  getThreat: async (id: string): Promise<Threat> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/threats/${id}`));
  },

  quarantineThreat: async (id: string): Promise<{ message: string; threat_id: string; status: string }> => {
    return _unwrap(await fetchClient.put(`/api/v1/cwpp/threats/${id}/quarantine`));
  },

  removeThreat: async (id: string): Promise<{ message: string; threat_id: string; status: string }> => {
    return _unwrap(await fetchClient.put(`/api/v1/cwpp/threats/${id}/remove`));
  },

  getThreatStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/threats/statistics'));
  },

  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cwpp/dashboard'));
  },

  // SBOM (Software Bill of Materials) - vuln-scanner child serves per-workload
  // SBOM at /api/v1/sbom/{workload_id} (+ /vulnerabilities). The gateway maps
  // /api/v1/cwpp/sbom → /api/v1/sbom on the cwpp aggregate (8005). The prior
  // /cwpp/workloads/{id}/sbom path had no handler on workload-service → empty.
  getSBOM: async (workloadId: string, format?: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/sbom/${workloadId}`, { format: format || 'cyclonedx' }));
  },

  getSBOMVulnerabilities: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/sbom/${workloadId}/vulnerabilities`));
  },

  // Patch Recommendations - routes to CWPP patch-management (real patch-recommendations
  // endpoint via gateway rewrite), NOT the vuln-scanner prioritized list which is a
  // different surface. Previously mislabeled to /vulnerabilities/prioritized.
  getPatchRecommendations: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/patch-management/recommendations', params));
  },

  // Risk Prioritization - routes to aggregator
  getRiskPrioritization: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/vulnerabilities/prioritized', params));
  },

  // Vulnerability Metrics - routes to aggregator
  getVulnMetrics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/vulnerabilities/statistics'));
  },

  // Vulnerability Exceptions - routes to aggregator
  getVulnExceptions: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/vulnerabilities/exceptions'));
  },

  // Process Tree - routes to aggregator
  getProcessTree: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/workloads/${encodeURIComponent(workloadId)}/processes`));
  },

  // Behavioral Baseline - routes to aggregator
  getBehavioralBaseline: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/workloads/${encodeURIComponent(workloadId)}/behavioral-baseline`));
  },

  // Anomalies - routes to aggregator
  getAnomalies: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/workloads/${encodeURIComponent(workloadId)}/anomalies`));
  },

  // Container Runtime Security - routes to aggregator
  getContainerRuntime: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/containers/runtime/${workloadId}`));
  },

  // Runtime Drift Detection - routes to aggregator
  getDriftDetection: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/workloads/${encodeURIComponent(workloadId)}/drift`));
  },

  // FIM Compliance Rules - routes via /api/v1/cwpp/fim prefix to fim service
  getFIMComplianceRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/fim/compliance-rules'));
  },

  // FIM Compliance Status
  getFIMComplianceStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/fim/compliance-status'));
  },

  // FIM Trends
  getFIMTrends: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/fim/trends'));
  },

  // YARA Rules - routes to aggregator which has threat data
  getYaraRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/threats/yara-rules'));
  },

  // IOCs (Indicators of Compromise) — R168: full CRUD via threat-intelligence
  // service. CWPP-prefixed read-list above is a different aggregator endpoint.
  getIOCs: async (params?: any): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/threats/iocs', params));
  },
  createIOC: async (data: { type: string; value: string; severity?: string; source?: string; tags?: string[]; expires_at?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/iocs', data));
  },
  bulkImportIOCs: async (data: { iocs: any[]; source?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/iocs/bulk', data));
  },
  deleteIOC: async (iocId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/iocs/${iocId}`));
  },
  enrichIOC: async (iocId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/iocs/${iocId}/enrich`, {}));
  },
  lookupIOC: async (params: { value: string; type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/iocs/lookup', params));
  },

  // MITRE ATT&CK Coverage
  getMitreCoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/threats/mitre-coverage'));
  },

  // Threat Hunting Queries
  getHuntingQueries: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/threats/hunting-queries'));
  },

  // Threat Intel Feeds
  getThreatIntelFeeds: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/threats/intel-feeds'));
  },

  // Threat Timeline
  getThreatTimeline: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/threats/timeline'));
  },

  // Host Config Assessment - Hardening Recommendations
  getHardeningRecommendations: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/host-config/recommendations/${workloadId}`));
  },

  // Host Config Assessment - Compliance Trends
  getConfigComplianceTrends: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/host-config/compliance-trends'));
  },

  // R79 — Host Hardening (CIS Benchmark + kernel security + service hardening)
  getCISBenchmark: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/host-config/cis-benchmark/${workloadId}`));
  },
  getKernelSecurity: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/host-config/kernel-security/${workloadId}`));
  },
  getServiceHardening: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/host-config/service-hardening/${workloadId}`));
  },

  // R80 — Workload Identity / RBAC over-privilege (proxies KSPM RBAC routes)
  getRBACOverview: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/overview'));
  },
  getOverPrivilegedIdentities: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/over-privileged-identities'));
  },
  getRiskyPermissions: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/risky-permissions'));
  },
  getServiceAccounts: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/service-accounts'));
  },
  getRBACRiskScores: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/risk-scores'));
  },

  // Agentless Scanner - Coverage
  getScanCoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/agentless/coverage'));
  },

  // Secret Scanner - Rotation Status. The secret-scanner child serves these at
  // bare /api/v1/rotation-status|rotation-policies|exposure-analysis. Routing
  // them under /cwpp/secrets/* collided with /api/v1/secrets/{secret_id}
  // (the literal segment was captured as an id), so call via the /cwpp catch-all.
  getSecretRotationStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/rotation-status'));
  },

  // Secret Scanner - Rotation Policies
  getSecretRotationPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/rotation-policies'));
  },

  // Secret Scanner - Exposure Analysis
  getSecretExposureAnalysis: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/exposure-analysis'));
  },

  // Attack Path Analyzer - Blast Radius
  getBlastRadius: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/attack-paths/blast-radius/${workloadId}`));
  },

  // Attack Path Analyzer - Toxic Combinations
  getToxicCombinations: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/attack-paths/toxic-combinations'));
  },

  // Attack Path Analyzer - Risk Simulation
  runRiskSimulation: async (scenario: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cwpp/attack-paths/risk-simulation', scenario));
  },

  // ---- Patch Management ----
  getPatchManagementRecommendations: async (params?: {
    severity?: string;
    workload_id?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/patch-management/recommendations', params));
  },

  applyPatch: async (patchData: {
    patch_id: string;
    workload_ids: string[];
    maintenance_window?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cwpp/patch-management/apply', patchData));
  },

  getPatchHistory: async (params?: { workload_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/patch-management/history', params));
  },

  // ---- Network Connections ----
  // BE (workload-service) serves the per-workload network view at
  // /api/v1/workloads/{workload_id}/network (main.py:1010), NOT /network-connections.
  getWorkloadNetworkConnections: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/workloads/${encodeURIComponent(workloadId)}/network`));
  },

  // ---- Per-Workload Compliance ----
  getWorkloadCompliance: async (workloadId: string): Promise<any> => {
    if (!isPathSafeWorkloadId(workloadId)) throw unroutableWorkloadId(workloadId);
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/workloads/${encodeURIComponent(workloadId)}/compliance`));
  },

  // ---- Segmentation Analysis ----
  // BE (agentless-scanner) exposes /api/v1/segmentation/analyze as POST only
  // (segmentation.py:41). The prior GET to /segmentation/analysis 405'd. Fleet-wide
  // analysis uses an empty request body (all fields optional on the BE model).
  getSegmentationAnalysis: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cwpp/segmentation/analyze', {}));
  },

  getWorkloadSegmentation: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/segmentation/workload/${workloadId}`));
  },

  // ---- FIM Baseline Management ----
  createFIMBaseline: async (workloadId: string, paths?: string[]): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cwpp/fim/baselines/${workloadId}`, {
      paths: paths || ['/etc', '/usr/bin', '/usr/sbin'],
    }));
  },

  getFIMStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/fim/statistics'));
  },

  // ---- Bulk Auto-Remediation ----
  bulkAutoRemediate: async (params: {
    finding_ids?: string[];
    categories?: string[];
    severity_threshold?: string;
    dry_run?: boolean;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cwpp/remediation/auto-remediate', params));
  },

  // ---- Workload Discovery ----
  discoverWorkloads: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cwpp/workloads/discover'));
  },

  discoveryStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/workloads/discover/status'));
  },

  // Segmentation
  generateSegmentationPolicy: (data: { namespace: string; pod_labels: Record<string, string>; allowed_ports?: number[]; allowed_cidrs?: string[]; allowed_namespaces?: string[] }) =>
    fetchClient.post('/api/v1/cwpp/segmentation/generate-policy', data).then(_thenUnwrap),
  analyzeSegmentation: (data: { workload_id?: string; open_ports?: number[] }) =>
    fetchClient.post('/api/v1/cwpp/segmentation/analyze', data).then(_thenUnwrap),
  getSegmentationRecommendations: () =>
    fetchClient.get('/api/v1/cwpp/segmentation/recommendations').then(_thenUnwrap),
  // Forensics
  buildForensicTimeline: (data: { scan_id: string }) =>
    fetchClient.post('/api/v1/cwpp/forensics/timeline/build', data).then(_thenUnwrap),
  getForensicTimeline: (workloadId: string, params?: { time_range_hours?: number }) =>
    fetchClient.get(`/api/v1/cwpp/forensics/timeline/${workloadId}`, params).then(_thenUnwrap),
  preserveForensicSnapshot: (data: { instance_id: string; provider: string }) =>
    fetchClient.post('/api/v1/cwpp/forensics/snapshot/preserve', data).then(_thenUnwrap),
  getForensicEvidence: (params?: { case_id?: string; instance_id?: string }) =>
    fetchClient.get('/api/v1/cwpp/forensics/evidence', params).then(_thenUnwrap),
};

export const kspmApi = {
  // Core
  getStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/statistics'));
  },
  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/dashboard'));
  },
  getStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/stats'));
  },
  getTrends: async (days?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/trends', { days }));
  },
  exportFindings: async (params?: { cluster_id?: string; severity?: string; format?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/export', params));
  },

  // Clusters
  getClusters: async (params?: { provider?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/clusters', params));
  },
  getCluster: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}`));
  },
  getClusterScore: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/score`));
  },
  assessCluster: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/kspm/clusters/${clusterId}/assess`));
  },
  deleteCluster: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/kspm/clusters/${clusterId}`));
  },
  registerCluster: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/clusters', data));
  },

  // Scan
  triggerScan: async (clusterId: string): Promise<any> => {
    // Wiz-parity: unified data-plane primitive. Gateway proxies to
    // kspm /api/v1/kspm/scan. cluster_id is passed in the body since the
    // consolidated primitive uses a POST body, not a path segment.
    return _unwrap(await fetchClient.post('/api/v1/data-plane/refresh/kspm', { cluster_id: clusterId }));
  },
  triggerFullScan: async (data?: { cluster_id?: string; frameworks?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/scan', data || {}));
  },

  // Cluster Security
  getClusterSecurityChecks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/cluster-security/checks'));
  },
  getClusterSecurityResults: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/cluster-security/results/${clusterId}`));
  },
  evaluateClusterConfig: async (config: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/cluster-security/evaluate', config));
  },

  // Findings
  getFindings: async (params?: { cluster_id?: string; severity?: string; category?: string; status?: string; page?: number; page_size?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/findings', params));
  },
  updateFinding: async (findingId: string, data: { status: string }): Promise<any> => {
    return _unwrap(await fetchClient.patch(`/api/v1/kspm/findings/${findingId}`, data));
  },
  bulkUpdateFindings: async (data: { finding_ids: string[]; status: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/findings/bulk-update', data));
  },

  // Workloads
  getWorkloads: async (params?: { cluster_id?: string; privileged?: boolean; namespace?: string; severity?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads', params));
  },
  getWorkloadDetail: async (namespace: string, name: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/workloads/${namespace}/${name}`));
  },
  getWorkloadViolations: async (params?: { cluster_id?: string; severity?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads/violations', params));
  },
  scanWorkloads: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/workloads/scan', null, { cluster_id: clusterId }));
  },

  // RBAC
  getRbacOverview: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/overview', { cluster_id: clusterId }));
  },
  getRbacRoles: async (params?: { cluster_id?: string; min_risk?: number; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/roles', params));
  },
  getRbacBindings: async (params?: { cluster_id?: string; is_cluster_admin?: boolean; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/bindings', params));
  },
  getServiceAccounts: async (params?: { cluster_id?: string; is_default?: boolean; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/service-accounts', params));
  },
  getRiskyPermissions: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/risky-permissions', { cluster_id: clusterId }));
  },
  analyzeRbac: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/rbac/analyze', null, { cluster_id: clusterId }));
  },

  // Network
  getNetworkPolicies: async (params?: { cluster_id?: string; namespace?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/network/policies', params));
  },
  getNetworkExposure: async (params?: { cluster_id?: string; is_exposed?: boolean; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/network/exposure', params));
  },
  getNetworkSegmentation: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/network/segmentation', { cluster_id: clusterId }));
  },
  analyzeNetwork: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/network/analyze', null, { cluster_id: clusterId }));
  },

  // Images
  getImages: async (params?: { cluster_id?: string; is_latest_tag?: boolean; is_trusted?: boolean; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/images', params));
  },
  getImageVulnerabilities: async (digest: string, severity?: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/images/${digest}/vulnerabilities`, { severity }));
  },
  getRegistryPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/images/registries'));
  },
  createRegistryPolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/images/registries', data));
  },
  scanImage: async (data: { image: string; cluster_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/images/scan', data));
  },

  // Runtime
  getRuntimeAlerts: async (params?: { cluster_id?: string; alert_type?: string; severity?: string; status?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/runtime/alerts', params));
  },
  createRuntimeAlert: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/runtime/alerts', data));
  },
  getSuspiciousProcesses: async (params?: { cluster_id?: string; is_suspicious?: boolean; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/runtime/processes', params));
  },
  getContainerDrift: async (params?: { cluster_id?: string; drift_type?: string; severity?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/runtime/drift', params));
  },
  createRuntimeBaseline: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/runtime/baseline', data));
  },

  // Compliance
  getCompliance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/compliance'));
  },
  getComplianceFrameworks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/compliance/frameworks'));
  },
  getComplianceScore: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/compliance/${frameworkId}/score`));
  },
  getComplianceControls: async (frameworkId: string, status?: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/compliance/${frameworkId}/controls`, { status }));
  },
  evaluateCompliance: async (data: { frameworks?: string[]; cluster_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/compliance/evaluate', data));
  },

  // Admission Control
  getAdmissionPolicies: async (enabled?: boolean): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/admission/policies', { enabled }));
  },
  createAdmissionPolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/admission/policies', data));
  },
  updateAdmissionPolicy: async (policyId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/kspm/admission/policies/${policyId}`, data));
  },
  deleteAdmissionPolicy: async (policyId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/kspm/admission/policies/${policyId}`));
  },
  validateManifest: async (data: { manifest: any; namespace?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/admission/validate', data));
  },
  // R172-A (2026-05-19): added page_size to match BE signature
  // (services/domain/kspm/app/routers/admission.py:401 page_size: ge=1, le=200).
  // Required by AdmissionPoliciesPanel in KSPMDashboard.tsx which requests
  // 100 violations per fetch.
  getAdmissionViolations: async (params?: { cluster_id?: string; policy_id?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/admission/violations', params));
  },

  // Cluster Security — extended
  getClusterSecurityPosture: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/security-posture`));
  },
  updateClusterSecurityPosture: async (clusterId: string, config: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/kspm/clusters/${clusterId}/security-posture`, config));
  },
  getClusterMisconfigurations: async (clusterId: string, params?: { severity?: string; category?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/misconfigurations`, params));
  },
  getClusterEvents: async (clusterId: string, params?: { event_type?: string; severity?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/events`, params));
  },
  runBenchmark: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/kspm/clusters/${clusterId}/benchmark`));
  },
  getBenchmarkResults: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/benchmark/results`));
  },
  getClusterNodes: async (clusterId: string, params?: { status?: string; role?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/nodes`, params));
  },
  getNodeDetail: async (clusterId: string, nodeId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/nodes/${nodeId}`));
  },
  getApiServerAudit: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/clusters/${clusterId}/api-server-audit`));
  },

  // Workload Security — extended
  getWorkloadSecurity: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/workloads/${workloadId}/security`));
  },
  getWorkloadVulnerabilities: async (params?: { cluster_id?: string; severity?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads/vulnerabilities', params));
  },
  getPrivilegedWorkloads: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads/privileged', params));
  },
  getHostAccessWorkloads: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads/host-access', params));
  },
  scanWorkload: async (workloadId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/kspm/workloads/${workloadId}/scan`));
  },
  getWorkloadDrift: async (params?: { cluster_id?: string; severity?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads/drift', params));
  },
  getSecretsExposure: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads/secrets-exposure', params));
  },
  getResourceLimits: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/workloads/resource-limits', params));
  },

  // RBAC — extended
  getClusterRoles: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/cluster-roles', params));
  },
  getOverPrivileged: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/rbac/over-privileged', params));
  },

  // Network — extended
  getNetworkOverview: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/network/overview', { cluster_id: clusterId }));
  },
  getUnprotectedNamespaces: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/network/namespaces-without-policies', { cluster_id: clusterId }));
  },
  getIngressAnalysis: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/network/ingress', params));
  },
  getEgressAnalysis: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/network/egress', params));
  },
  simulateNetworkPolicy: async (data: { source_namespace: string; dest_namespace: string; port?: number; protocol?: string; cluster_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/network/simulate', data));
  },

  // Images — extended
  getImagesOverview: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/images/overview', { cluster_id: clusterId }));
  },
  getImageVulnerabilitiesList: async (params?: { severity?: string; exploitable?: boolean; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/images/vulnerabilities', params));
  },
  getUnsignedImages: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/images/unsigned', params));
  },
  getOutdatedImages: async (params?: { cluster_id?: string; max_age_days?: number; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/images/outdated', params));
  },
  scanSpecificImage: async (imageId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/kspm/images/${imageId}/scan`));
  },

  // Runtime — extended
  getRuntimeOverview: async (clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/runtime/overview', { cluster_id: clusterId }));
  },
  getFileIntegrity: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/runtime/file-integrity', params));
  },
  getNetworkAnomalies: async (params?: { cluster_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/runtime/network-anomalies', params));
  },
  createRuntimePolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/runtime/policies', data));
  },

  // Admission — extended
  getAdmissionOverview: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/admission/overview'));
  },

  // Compliance — extended
  getComplianceStatus: async (frameworkId: string, clusterId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/kspm/compliance/${frameworkId}/status`, { cluster_id: clusterId }));
  },
  runComplianceScan: async (data: { framework_id?: string; cluster_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/compliance/scan', data));
  },
  getComplianceTrends: async (params?: { framework_id?: string; days?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/compliance/trends', params));
  },
  getComplianceGaps: async (params?: { framework_id?: string; cluster_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/compliance/gaps', params));
  },
};

export const kspmClusterApi = {
  registerCluster: async (data: { name: string; provider: string; role_arn?: string; region?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/clusters/register', data));
  },
  registerClusterSimple: async (data: { name: string; context?: string; provider?: string; region?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/kspm/clusters', data));
  },
  listClusters: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kspm/clusters'));
  },
  deleteCluster: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/kspm/clusters/${clusterId}`));
  },
};

export const snapshotScanApi = {
  triggerScan: (params: {
    instance_id?: string;
    volume_id?: string;
    snapshot_id?: string;
    provider?: string;
    region?: string;
    role_arn?: string;
    scan_packages?: boolean;
    scan_secrets?: boolean;
    scan_misconfigs?: boolean;
  }) => fetchClient.post('/api/v1/snapshot-scan', params).then(_thenUnwrap),

  getStatus: (scanId: string) =>
    fetchClient.get(`/api/v1/snapshot-scan/${scanId}`).then(_thenUnwrap),

  // R152: the second argument to apiClient.get IS the params map, not an
  // axios-style options object. Wrapping it in `{ params: ... }` made the
  // builder do `String({page:1,...})`, i.e. append a single query parameter
  // literally named `params` with the value `[object Object]` — so page,
  // page_size and severity were never sent and the server silently applied its
  // defaults. Paging showed page 1 forever and a severity filter returned
  // everything. (R147 fixed 44 of these; these three survived.)
  getPackages: (scanId: string, page = 1, pageSize = 100) =>
    fetchClient.get(`/api/v1/snapshot-scan/${scanId}/packages`, { page, page_size: pageSize }).then(_thenUnwrap),

  getVulnerabilities: (scanId: string, severity?: string) =>
    fetchClient.get(`/api/v1/snapshot-scan/${scanId}/vulnerabilities`, severity ? { severity } : {}).then(_thenUnwrap),

  getSecrets: (scanId: string) =>
    fetchClient.get(`/api/v1/snapshot-scan/${scanId}/secrets`).then(_thenUnwrap),

  getMisconfigs: (scanId: string) =>
    fetchClient.get(`/api/v1/snapshot-scan/${scanId}/misconfigs`).then(_thenUnwrap),

  listScans: (status?: string, limit = 50) =>
    fetchClient.get('/api/v1/snapshot-scans', { ...(status ? { status } : {}), limit }).then(_thenUnwrap),

  triggerBulkScan: (instanceIds: string[], provider = 'aws', region = 'us-east-1', roleArn?: string) =>
    fetchClient.post('/api/v1/snapshot-scan/bulk', { instance_ids: instanceIds, provider, region, role_arn: roleArn }).then(_thenUnwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// Scan-broker coverage — WHETHER A DISK WAS READ AT ALL.
//
// WHY THIS EXISTS. `GET /api/v1/scan-brokers/coverage` has answered the one
// question the vulnerability pillar cannot answer for itself since 2026-08-10 —
// *were these workloads actually inspected, or merely enumerated* — and a grep
// for `scan-brokers` over the whole of frontend/src returned ZERO hits until
// 2026-08-12. Exactly the shape of ProviderCoverage.tsx before it was written,
// and of `tenant_id_dep` before it had callers: the API told the truth and
// nothing rendered it.
//
// What that cost: with no live outpost the enqueuer produces jobs that sit at
// `queued`, nothing claims them, nothing errors, and the UI shows workloads
// carrying no vulnerability findings — which reads as CLEAN. On this estate the
// honest number is 15 of 15 workloads NOT ASSESSED.
// ─────────────────────────────────────────────────────────────────────────────
export interface ScanCoverageScope {
  cloud_provider: string;
  account_id: string;
  region: string;
  workload_count: number;
  /** null means no outpost is registered for this scope at all. */
  outpost: Record<string, any> | null;
  /** 'not_assessed' | 'covered' | … — rendered verbatim, never inferred. */
  disk_scanning: string;
  /** The backend's own sentence explaining the verdict. Rendered as-is. */
  reason?: string | null;
  queued_jobs?: number;
  last_completed_at?: string | null;
}

export interface ScanCoverage {
  scopes: ScanCoverageScope[];
  scopes_total: number;
  scopes_not_assessed: number;
  workloads_total: number;
  workloads_not_assessed: number;
  brokers_registered: Array<Record<string, any>>;
  /** A registered-but-stale broker means "restart what you deployed"; an
   *  absent outpost means "deploy one". Different actions — keep them apart. */
  brokers_registered_stale: number;
  jobs_pinned_to_stale_broker?: number;
  heartbeat_stale_seconds?: number;
  disk_scannable_resource_types?: string[];
}

export const scanCoverageApi = {
  get: (): Promise<ScanCoverage> =>
    fetchClient.get('/api/v1/scan-brokers/coverage').then(_thenUnwrap) as Promise<ScanCoverage>,
};

// R125: imageDriftApi realigned to match actual BE routes on
// container-security runtime-security service. Previous wrapper used
// /api/v1/container/drift/* (singular) but BE serves the plural
// /api/v1/containers/drift/* path at
// services/domain/container-security/services/runtime-security/app/main.py:3995-4225.
// Removed 6 methods that had no BE handler (updatePolicy / deletePolicy /
// togglePolicy / getEvents / getEventDetail / triggerScan / getStats /
// getBaseline / setBaseline) — those endpoints were spec-driven, never
// implemented. Replaced with the 5 endpoints that DO exist:
// policy CRUD (create + list), evaluate, enforce, enforcement-log.
// Image-baseline preserved via runtime-sensor /api/v1/image-drift/{baseline,check}.
export const imageDriftApi = {
  // Policies (csp_container.drift_policies)
  createPolicy: (data: {
    name: string; description?: string; action?: 'alert' | 'block' | 'kill';
    enabled?: boolean; target_namespaces?: string[]; target_labels?: Record<string, string>;
    allowed_binaries?: string[]; allowed_network_destinations?: string[];
    block_new_executables?: boolean; block_new_network_connections?: boolean;
  }) =>
    fetchClient.post('/api/v1/containers/drift/policy', data).then(_thenUnwrap),
  getPolicies: () =>
    fetchClient.get('/api/v1/containers/drift/policies').then(_thenUnwrap),

  // Evaluation + enforcement
  evaluateDrift: (data: {
    container_id: string; container_name?: string; namespace?: string;
    pod_name?: string; observed_processes?: string[]; observed_connections?: string[];
  }) =>
    fetchClient.post('/api/v1/containers/drift/evaluate', data).then(_thenUnwrap),
  enforceDrift: (data: { container_id: string; policy_id: string; action: 'alert' | 'block' | 'kill' }) =>
    fetchClient.post('/api/v1/containers/drift/enforce', data).then(_thenUnwrap),
  getEnforcementLog: (params?: { policy_id?: string; container_id?: string; limit?: number }) =>
    fetchClient.get('/api/v1/containers/drift/enforcement-log', params).then(_thenUnwrap),

  // Image baseline (runtime-sensor :8125)
  setImageBaseline: (containerId: string, files: Record<string, string>) =>
    fetchClient.post(`/api/v1/image-drift/baseline?container_id=${encodeURIComponent(containerId)}`, files).then(_thenUnwrap),
  checkImageDrift: (containerId: string, currentFiles: Record<string, string>) =>
    fetchClient.post(`/api/v1/image-drift/check?container_id=${encodeURIComponent(containerId)}`, currentFiles).then(_thenUnwrap),
};

export const runtimeSensorApi = {
  getDashboard: () => fetchClient.get('/api/v1/runtime/dashboard').then(_thenUnwrap),
  getSensors: (params?: any) => fetchClient.get('/api/v1/runtime/sensors', params).then(_thenUnwrap),
  getAlerts: (params?: any) => fetchClient.get('/api/v1/runtime/alerts', params).then(_thenUnwrap),
  getThreatRules: (params?: any) => fetchClient.get('/api/v1/runtime/threat-rules', params).then(_thenUnwrap),
  getMitreCoverage: () => fetchClient.get('/api/v1/runtime/threat-rules/mitre-coverage').then(_thenUnwrap),
};
